import { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import useTotalReviewProgress from '../pages/assignment/components/assignment-header/question-progress/useTotalReviewProgress'
import { useVersionId } from '../utils/parseURL.helper'
import useQueryParamSetter from '../hooks/useQueryParamSetter'
import { ASSIGNEE_IDS_PARAM, DASHBOARD_TYPE_PARAM, OPEN_SUBMISSION_ID_PARAM, RESUME_FROM_BUTTON_PARAM } from '../constants/query-params'
import Routes from '../constants/routes'
import { AssignmentSections } from '../constants/assignment-sections'
import { submissionActions } from '../store/slices/submission.slice'
import { textSubmissionDrawerActions } from '../store/slices/text-submission-drawer.slice'
import { useTextSubmissionNavigation } from '../store/apis/utils/hooks/text-submission.hooks'
import useSubmissionGroups from '../pages/assignment/sections/submissions/submission-groups/useSubmissionGroups'
import submissionSelectors from '../store/selectors/submission.selectors'
import assignmentSelectors from '../store/selectors/assignment.selectors'
import userSelectors from '../store/selectors/user.selectors'
import LocalStorageService from '../services/local-storage.service'
import { computeFirstUnreviewedQuestionRubric, computeNextUnreviewedQuestionRubric, findFirstEarlierUnapprovedSubmissionId } from './reviewFlow.helpers'
import { isNumber } from 'lodash'
import useFirstUnreviewedSubmission from '../pages/assignment/components/assignment-header/explore-submissions-button/useFirstUnreviewedSubmission'

const useReviewFlow = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { token, layerId: layerIdParam } = useParams()
  const versionId = useVersionId()
  const setQueryParam = useQueryParamSetter()

  const { statsPerQuestionRubric } = useTotalReviewProgress()
  const currentQuestionRubricId = Number(layerIdParam)

  const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)
  const isOwner = useSelector(assignmentSelectors.isOwner)
  const userId = useSelector(userSelectors.id)
  const { groups } = useSubmissionGroups({ applyFilter: false })

  const { navigateToNextSubmission, nextSubmissionExists } = useTextSubmissionNavigation()

  const nextQuestionRubricId = useMemo(() => {
    if (!isNumber(currentQuestionRubricId) || Number.isNaN(currentQuestionRubricId)) return undefined
    return computeNextUnreviewedQuestionRubric(currentQuestionRubricId, statsPerQuestionRubric)
  }, [currentQuestionRubricId, statsPerQuestionRubric])

  const hasEarlierSkipped = useMemo(() => {
    const firstEarlierSkippedId = findFirstEarlierUnapprovedSubmissionId(groups, openedSubmissionId)
    return Boolean(firstEarlierSkippedId)
  }, [groups, openedSubmissionId])

  // Reviewers must only see submissions assigned to them; owners only when in reviewer dashboard mode
  const buildSearchWithAssignee = useCallback(() => {
    const params = new URLSearchParams(location.search)
    const isReviewerView = !isOwner || params.get(DASHBOARD_TYPE_PARAM) === 'reviewer'
    if (!isReviewerView || !userId) return location.search
    params.set(ASSIGNEE_IDS_PARAM, userId)
    return `?${params.toString()}`
  }, [isOwner, userId])

  const continueToNextQuestionRubric = useCallback(() => {
    if (!nextQuestionRubricId) return
    const searchWithAssignee = buildSearchWithAssignee()
    navigate({
      pathname: `${Routes.ASSIGNMENTS}/${token}/${nextQuestionRubricId}/${AssignmentSections.HOME}`,
      search: searchWithAssignee,
    })
  }, [navigate, token, nextQuestionRubricId, buildSearchWithAssignee])

  const returnToFirstSkipped = useCallback(() => {
    const firstSkippedId = findFirstEarlierUnapprovedSubmissionId(groups, openedSubmissionId)
    if (firstSkippedId) {
      dispatch(submissionActions.openSubmission({ id: firstSkippedId, token, versionId, questionRubricId: currentQuestionRubricId }))
      if (isNumber(currentQuestionRubricId)) dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(currentQuestionRubricId))
    }
  }, [groups, openedSubmissionId, dispatch, token, versionId, currentQuestionRubricId])

  const firstUnreviewedQuestionRubricId = useMemo(() => computeFirstUnreviewedQuestionRubric(statsPerQuestionRubric), [statsPerQuestionRubric])
  const { firstUnreviewedSubmission, isFetching: isFetchingFirstUnreviewedSubmission } = useFirstUnreviewedSubmission(firstUnreviewedQuestionRubricId)

  const startOrContinue = useCallback(() => {
    // Try last visited submission first
    const lastContext = LocalStorageService.getLastVisitedReviewContext(token, versionId)
    if (lastContext?.submissionId) {
      setQueryParam({ [OPEN_SUBMISSION_ID_PARAM]: lastContext.submissionId, [RESUME_FROM_BUTTON_PARAM]: 'true' })
    } else {
      // Open first unreviewed submission for first unreviewed questionRubric if possible
      if (firstUnreviewedSubmission?.id) {
        setQueryParam({ [OPEN_SUBMISSION_ID_PARAM]: firstUnreviewedSubmission.id, [RESUME_FROM_BUTTON_PARAM]: 'true' })
      }
    }

    const targetLayerId = lastContext?.questionRubricId || firstUnreviewedQuestionRubricId || 'all'
    const searchWithAssignee = buildSearchWithAssignee()
    navigate({
      pathname: `${Routes.ASSIGNMENTS}/${token}/${targetLayerId}/${AssignmentSections.HOME}`,
      search: searchWithAssignee,
    })
  }, [navigate, setQueryParam, firstUnreviewedSubmission, firstUnreviewedQuestionRubricId, token, versionId, buildSearchWithAssignee])

  const openNextSubmissionOrPrompt = useCallback(() => {
    if (nextSubmissionExists) {
      navigateToNextSubmission()
      return
    }
    // End of list
    if (hasEarlierSkipped && nextQuestionRubricId) {
      // Approach A: allow advancing; UI may optionally prompt
      continueToNextQuestionRubric()
      return
    }
    if (nextQuestionRubricId) {
      continueToNextQuestionRubric()
    }
  }, [nextSubmissionExists, navigateToNextSubmission, hasEarlierSkipped, nextQuestionRubricId, continueToNextQuestionRubric])

  return {
    // state
    currentQuestionRubricId: isNumber(currentQuestionRubricId) ? currentQuestionRubricId : undefined,
    hasEarlierSkipped,
    nextQuestionRubricId,
    canContinueToNextQuestionRubric: Boolean(nextQuestionRubricId),
    hasNextSubmission: nextSubmissionExists,
    isFetchingFirstUnreviewedSubmission,

    // actions
    startOrContinue,
    openNextSubmissionOrPrompt,
    continueToNextQuestionRubric,
    returnToFirstSkipped,
  }
}

export default useReviewFlow


